import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { liveApi } from '../services/api';

// Sport-specific display config — add entries as new scoring engines are added
const SPORT_CONFIG = {
  badminton:    { serviceEmoji: '🏸', gameLabel: 'Game',  gamesLabel: 'Games' },
  tennis:       { serviceEmoji: '🎾', gameLabel: 'Set',   gamesLabel: 'Sets'  },
  table_tennis: { serviceEmoji: '🏓', gameLabel: 'Game',  gamesLabel: 'Games' },
  squash:       { serviceEmoji: '🟡', gameLabel: 'Game',  gamesLabel: 'Games' },
  pickleball:   { serviceEmoji: '🥒', gameLabel: 'Game',  gamesLabel: 'Games' },
  padel:        { serviceEmoji: '🎾', gameLabel: 'Set',   gamesLabel: 'Sets'  },
};
const DEFAULT_SPORT_CONFIG = { serviceEmoji: '●', gameLabel: 'Game', gamesLabel: 'Games' };
const getSportConfig = (sport) => SPORT_CONFIG[(sport || '').toLowerCase()] || DEFAULT_SPORT_CONFIG;

const LiveScoringPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();

  const [liveScore, setLiveScore] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [showStartOptions, setShowStartOptions] = useState(false);
  const [initialServer, setInitialServer] = useState('p1');
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const tapFeedback = useRef(null);

  // ── Load initial state ────────────────────────────────────────────────────

  const loadState = useCallback(async () => {
    try {
      const res = await liveApi.getLiveState(matchId);
      const data = res.data;
      setMatchInfo(data);
      if (data.is_live && data.live_score) {
        setLiveScore(data.live_score);
      } else {
        setShowStartOptions(true);
      }
    } catch {
      setError('Match not found or access denied.');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { loadState(); }, [loadState]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleStart = async () => {
    setWorking(true);
    try {
      const res = await liveApi.startLive(matchId, { initialServer });
      setLiveScore(res.data.live_score);
      setShowStartOptions(false);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start live scoring');
    } finally { setWorking(false); }
  };

  const handlePoint = async (player) => {
    if (working || !liveScore || liveScore.matchOver || liveScore.pausedAfterGame || liveScore.activeChallenge) return;
    setWorking(true);
    tapFeedback.current = player;
    try {
      const res = await liveApi.addPoint(matchId, player);
      setLiveScore(res.data.live_score);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to record point');
    } finally {
      setWorking(false);
      tapFeedback.current = null;
    }
  };

  const handleUndo = async () => {
    if (working) return;
    setWorking(true);
    try {
      const res = await liveApi.undoPoint(matchId);
      setLiveScore(res.data.live_score);
    } catch (e) {
      setError(e.response?.data?.error || 'Nothing to undo');
    } finally { setWorking(false); }
  };

  const handleNextGame = async () => {
    if (working) return;
    setWorking(true);
    try {
      const res = await liveApi.nextGame(matchId);
      setLiveScore(res.data.live_score);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start next game');
    } finally { setWorking(false); }
  };

  const handleRaiseChallenge = async (player) => {
    if (working) return;
    setWorking(true);
    try {
      const res = await liveApi.raiseChallenge(matchId, player);
      setLiveScore(res.data.live_score);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to raise challenge');
    } finally { setWorking(false); }
  };

  const handleChallengeResult = async (outcome) => {
    if (working) return;
    setWorking(true);
    try {
      const res = await liveApi.resolveChallengeOutcome(matchId, outcome);
      setLiveScore(res.data.live_score);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to resolve challenge');
    } finally { setWorking(false); }
  };

  const handleEnd = async () => {
    setWorking(true);
    try {
      await liveApi.endLive(matchId);
      navigate(-1);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to end match');
      setShowEndConfirm(false);
    } finally { setWorking(false); }
  };

  // ── Derived state helpers ─────────────────────────────────────────────────

  const p1Name = matchInfo?.player1_name || 'Player 1';
  const p2Name = matchInfo?.player2_name || 'Player 2';
  const sportConfig = getSportConfig(liveScore?.sport || matchInfo?.sport);

  const isServing = (player) => liveScore?.servingPlayer === player;

  const challengesLeft = (player) => liveScore?.challenges?.[player] ?? (liveScore?.settings?.challengesPerGame ?? 2);

  const lastPointPlayer = liveScore?.history?.length
    ? liveScore.history[liveScore.history.length - 1]?.player
    : null;

  const getDeuceLabel = () => {
    if (!liveScore || liveScore.pausedAfterGame) return '';
    const { currentPoints, settings } = liveScore;
    const pts = settings?.pointsPerSet || 21;
    const cap = pts === 15 ? 21 : 30;
    const deuceAt = pts - 1;
    const max = Math.max(currentPoints.p1, currentPoints.p2);
    const min = Math.min(currentPoints.p1, currentPoints.p2);
    if (max === cap - 1 && min === cap - 1) return '⚡ Golden Point next!';
    if (max >= deuceAt && max === min) return 'Deuce!';
    if (max >= deuceAt && max > min) {
      const leader = currentPoints.p1 > currentPoints.p2 ? p1Name : p2Name;
      return `Advantage ${leader}`;
    }
    return '';
  };

  const gamesDisplay = liveScore?.games || [];
  const gamesSummary = gamesDisplay.map((g, i) => (
    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: 'rgba(255,255,255,0.12)', borderRadius: 999, fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)' }}>
      {sportConfig.gameLabel[0]}{g.gameNumber}: {g.points.p1}–{g.points.p2}
    </span>
  ));

  // ── Shared styles ─────────────────────────────────────────────────────────

  const BG = 'linear-gradient(160deg, #134E4A 0%, #0F766E 100%)';

  // ── Loading / Error ───────────────────────────────────────────────────────

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  if (error && !liveScore && !showStartOptions) return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <p style={{ color: '#dc2626' }}>{error}</p>
      <button className="btn btn-outline" onClick={() => navigate(-1)}>← Back</button>
    </div>
  );

  // ── Start screen ──────────────────────────────────────────────────────────

  if (showStartOptions) {
    const startConfig = getSportConfig(matchInfo?.sport);
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
        <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 20, padding: '2.5rem', maxWidth: 400, width: '100%', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{startConfig.serviceEmoji}</div>
          <h2 style={{ color: 'white', margin: '0 0 0.35rem', fontWeight: 800, fontSize: '1.25rem' }}>Start Live Scoring</h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', marginBottom: '1.75rem' }}>
            {p1Name} vs {p2Name}
          </p>
          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', marginBottom: '0.75rem', fontWeight: 600 }}>Who serves first?</p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {['p1', 'p2'].map(p => (
                <button key={p} onClick={() => setInitialServer(p)}
                  style={{ flex: 1, padding: '0.65rem', borderRadius: 10, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
                    background: initialServer === p ? 'white' : 'rgba(255,255,255,0.1)',
                    color: initialServer === p ? '#0D9488' : 'rgba(255,255,255,0.7)',
                    border: initialServer === p ? 'none' : '1px solid rgba(255,255,255,0.2)' }}>
                  {startConfig.serviceEmoji} {p === 'p1' ? p1Name : p2Name}
                </button>
              ))}
            </div>
          </div>
          <button onClick={handleStart} disabled={working}
            style={{ width: '100%', padding: '0.8rem', borderRadius: 12, fontWeight: 800, fontSize: '1rem', cursor: working ? 'not-allowed' : 'pointer', background: 'white', color: '#0D9488', border: 'none', opacity: working ? 0.7 : 1 }}>
            {working ? 'Starting…' : '▶ Start Scoring'}
          </button>
          <button onClick={() => navigate(-1)} style={{ marginTop: '0.75rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '0.82rem' }}>
            ← Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Main scoring screen ───────────────────────────────────────────────────

  const score = liveScore;
  const gamesWon = score?.gamesWon || { p1: 0, p2: 0 };
  const currentPts = score?.currentPoints || { p1: 0, p2: 0 };
  const bestOf = score?.settings?.bestOf || 3;
  const gameHeader = `${sportConfig.gameLabel} ${score?.currentGame || 1} of ${bestOf}`;
  const deuceLabel = getDeuceLabel();
  const blocked = working || !!score?.pausedAfterGame || !!score?.activeChallenge || !!score?.matchOver;

  // Small challenge action button — placed below the undo button for each player
  const SmallChallengeBtn = ({ player }) => {
    const left = challengesLeft(player);
    const canChallenge = left > 0 && !score?.activeChallenge && !score?.matchOver && !working;
    return (
      <button
        onClick={() => canChallenge && handleRaiseChallenge(player)}
        disabled={!canChallenge}
        style={{
          padding: '0.3rem 0.5rem', borderRadius: 6, fontSize: '0.65rem', fontWeight: 600,
          background: 'transparent',
          border: `1px solid ${left > 0 && !score?.activeChallenge ? 'rgba(251,191,36,0.35)' : 'rgba(255,255,255,0.1)'}`,
          color: left > 0 && !score?.activeChallenge ? 'rgba(251,191,36,0.75)' : 'rgba(255,255,255,0.2)',
          cursor: canChallenge ? 'pointer' : 'default',
          letterSpacing: '0.04em',
        }}>
        {left > 0 ? `⚡ Challenge (${left} left)` : '⚡ No challenges left'}
      </button>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', userSelect: 'none' }}>

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.2)' }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '0.85rem' }}>
          ← Back
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'white', fontWeight: 700, fontSize: '0.88rem' }}>{p1Name} vs {p2Name}</div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>
            {score?.pausedAfterGame ? `${sportConfig.gameLabel} ${score.pausedAfterGame} complete — paused` : gameHeader}
          </div>
        </div>
        {score?.matchOver ? (
          <button onClick={() => setShowEndConfirm(true)}
            style={{ padding: '0.4rem 0.9rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            Save Result
          </button>
        ) : (
          <button onClick={() => setShowEndConfirm(true)}
            style={{ padding: '0.4rem 0.9rem', background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
            End Match
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ background: '#dc2626', color: 'white', padding: '0.5rem 1rem', fontSize: '0.82rem', textAlign: 'center' }}
          onClick={() => setError('')}>
          {error} — tap to dismiss
        </div>
      )}

      {/* Completed games + deuce label */}
      <div style={{ textAlign: 'center', padding: '0.75rem 1rem 0.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
          {gamesSummary}
        </div>
        {deuceLabel && (
          <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em' }}>{deuceLabel}</div>
        )}
        {score?.matchOver && (
          <div style={{ color: '#22c55e', fontWeight: 800, fontSize: '1rem', marginTop: '0.25rem' }}>
            🏆 {score.winner === 'p1' ? p1Name : p2Name} wins the match!
          </div>
        )}
      </div>

      {/* Games won */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '2.5rem', padding: '0.5rem 1rem', alignItems: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{sportConfig.gamesLabel}</div>
          <div style={{ color: 'white', fontWeight: 800, fontSize: '2.5rem', lineHeight: 1.1 }}>{gamesWon.p1}</div>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 700, fontSize: '1.5rem' }}>–</div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{sportConfig.gamesLabel}</div>
          <div style={{ color: 'white', fontWeight: 800, fontSize: '2.5rem', lineHeight: 1.1 }}>{gamesWon.p2}</div>
        </div>
      </div>

      {/* Main scoring columns */}
      <div style={{ display: 'flex', flex: 1, gap: '0.5rem', padding: '0.5rem 1rem', minHeight: 240 }}>

        {/* Player 1 column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {/* Score tap area */}
          <button
            onClick={() => handlePoint('p1')}
            disabled={blocked}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: score?.activeChallenge?.player === 'p1' ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.08)',
              borderRadius: 16,
              border: score?.activeChallenge?.player === 'p1' ? '2px solid rgba(251,191,36,0.55)' : '2px solid rgba(255,255,255,0.1)',
              cursor: blocked ? 'not-allowed' : 'pointer', gap: '0.4rem',
              transition: 'background 0.1s, border-color 0.2s', minHeight: 180,
              opacity: (score?.matchOver || score?.pausedAfterGame) ? 0.45 : 1,
              position: 'relative',
            }}
            onMouseDown={e => !blocked && (e.currentTarget.style.background = 'rgba(13,148,136,0.35)')}
            onMouseUp={e => (e.currentTarget.style.background = score?.activeChallenge?.player === 'p1' ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.08)')}
            onTouchStart={e => !blocked && (e.currentTarget.style.background = 'rgba(13,148,136,0.35)')}
            onTouchEnd={e => (e.currentTarget.style.background = score?.activeChallenge?.player === 'p1' ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.08)')}
          >
            {isServing('p1') && <span style={{ fontSize: '1.2rem' }}>{sportConfig.serviceEmoji}</span>}
            {/* CHALLENGE badge — shown when this player has an active challenge */}
            {score?.activeChallenge?.player === 'p1' && (
              <span style={{
                background: '#fbbf24', color: '#134E4A',
                fontSize: '0.55rem', fontWeight: 900, letterSpacing: '0.15em',
                padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase',
              }}>CHALLENGE</span>
            )}
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', maxWidth: 120, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p1Name}
            </span>
            <span style={{ color: 'white', fontWeight: 900, fontSize: 'clamp(3rem, 12vw, 5rem)', lineHeight: 1 }}>
              {currentPts.p1}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>
              {score?.pausedAfterGame ? 'Game paused' : 'Tap to +1'}
            </span>
          </button>
          {/* Undo P1 */}
          <button
            onClick={handleUndo}
            disabled={working || lastPointPlayer !== 'p1'}
            title="Undo last point scored by this player"
            style={{
              padding: '0.5rem', background: 'rgba(239,68,68,0.15)',
              border: `1px solid rgba(239,68,68,${lastPointPlayer === 'p1' ? '0.5' : '0.2'})`,
              borderRadius: 10, fontWeight: 700, fontSize: '0.85rem',
              color: lastPointPlayer === 'p1' ? '#fca5a5' : 'rgba(255,255,255,0.25)',
              cursor: (working || lastPointPlayer !== 'p1') ? 'not-allowed' : 'pointer',
              opacity: lastPointPlayer !== 'p1' ? 0.35 : 1, transition: 'opacity 0.15s',
            }}>
            − Undo {p1Name.split(' ')[0]}'s point
          </button>
          {/* Challenge P1 */}
          <SmallChallengeBtn player="p1" />
        </div>

        {/* Player 2 column */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <button
            onClick={() => handlePoint('p2')}
            disabled={blocked}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: score?.activeChallenge?.player === 'p2' ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.08)',
              borderRadius: 16,
              border: score?.activeChallenge?.player === 'p2' ? '2px solid rgba(251,191,36,0.55)' : '2px solid rgba(255,255,255,0.1)',
              cursor: blocked ? 'not-allowed' : 'pointer', gap: '0.4rem',
              transition: 'background 0.1s, border-color 0.2s', minHeight: 180,
              opacity: (score?.matchOver || score?.pausedAfterGame) ? 0.45 : 1,
            }}
            onMouseDown={e => !blocked && (e.currentTarget.style.background = 'rgba(13,148,136,0.35)')}
            onMouseUp={e => (e.currentTarget.style.background = score?.activeChallenge?.player === 'p2' ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.08)')}
            onTouchStart={e => !blocked && (e.currentTarget.style.background = 'rgba(13,148,136,0.35)')}
            onTouchEnd={e => (e.currentTarget.style.background = score?.activeChallenge?.player === 'p2' ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.08)')}
          >
            {isServing('p2') && <span style={{ fontSize: '1.2rem' }}>{sportConfig.serviceEmoji}</span>}
            {/* CHALLENGE badge — shown when this player has an active challenge */}
            {score?.activeChallenge?.player === 'p2' && (
              <span style={{
                background: '#fbbf24', color: '#134E4A',
                fontSize: '0.55rem', fontWeight: 900, letterSpacing: '0.15em',
                padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase',
              }}>CHALLENGE</span>
            )}
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', maxWidth: 120, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p2Name}
            </span>
            <span style={{ color: 'white', fontWeight: 900, fontSize: 'clamp(3rem, 12vw, 5rem)', lineHeight: 1 }}>
              {currentPts.p2}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>
              {score?.pausedAfterGame ? 'Game paused' : 'Tap to +1'}
            </span>
          </button>
          {/* Undo P2 */}
          <button
            onClick={handleUndo}
            disabled={working || lastPointPlayer !== 'p2'}
            title="Undo last point scored by this player"
            style={{
              padding: '0.5rem', background: 'rgba(239,68,68,0.15)',
              border: `1px solid rgba(239,68,68,${lastPointPlayer === 'p2' ? '0.5' : '0.2'})`,
              borderRadius: 10, fontWeight: 700, fontSize: '0.85rem',
              color: lastPointPlayer === 'p2' ? '#fca5a5' : 'rgba(255,255,255,0.25)',
              cursor: (working || lastPointPlayer !== 'p2') ? 'not-allowed' : 'pointer',
              opacity: lastPointPlayer !== 'p2' ? 0.35 : 1, transition: 'opacity 0.15s',
            }}>
            − Undo {p2Name.split(' ')[0]}'s point
          </button>
          {/* Challenge P2 */}
          <SmallChallengeBtn player="p2" />
        </div>
      </div>

      {/* Bottom bar: generic undo fallback */}
      <div style={{ padding: '0.5rem 1rem 1.5rem', display: 'flex', justifyContent: 'center' }}>
        <button
          onClick={handleUndo}
          disabled={working || !score?.history?.length}
          style={{
            padding: '0.45rem 1.25rem', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10,
            color: 'rgba(255,255,255,0.45)', fontWeight: 500, fontSize: '0.78rem',
            cursor: (working || !score?.history?.length) ? 'not-allowed' : 'pointer',
            opacity: !score?.history?.length ? 0.3 : 0.8,
          }}>
          ↩ Undo last point (any player)
        </button>
      </div>

      {/* ── Game-interval pause overlay ─────────────────────────────────────── */}
      {score?.pausedAfterGame && !score?.activeChallenge && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 50 }} />
          <div style={{
            position: 'fixed', inset: 0, zIndex: 51,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem',
          }}>
            <div style={{
              background: '#134E4A', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 20, padding: '2rem', maxWidth: 400, width: '100%', textAlign: 'center',
            }}>
              {/* Completed game result */}
              <div style={{ fontSize: '2rem', marginBottom: '0.4rem' }}>🏆</div>
              <h2 style={{ color: 'white', margin: '0 0 0.25rem', fontWeight: 800, fontSize: '1.3rem' }}>
                {sportConfig.gameLabel} {score.pausedAfterGame} Complete
              </h2>
              {(() => {
                const completedGame = score.games[score.pausedAfterGame - 1];
                const gameWinner = completedGame?.winner === 'p1' ? p1Name : p2Name;
                return (
                  <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', margin: '0 0 0.25rem' }}>
                    {completedGame ? `${completedGame.points.p1} – ${completedGame.points.p2}` : ''} · {gameWinner} wins this game
                  </p>
                );
              })()}
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', margin: '0 0 1.5rem' }}>
                {sportConfig.gamesLabel}: {p1Name} {gamesWon.p1} – {gamesWon.p2} {p2Name}
              </p>

              {/* Challenge the game-ending point */}
              <div style={{ marginBottom: '1.25rem' }}>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Challenge game-ending call?
                </p>
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  {[{ player: 'p1', name: p1Name }, { player: 'p2', name: p2Name }].map(({ player, name }) => {
                    const left = challengesLeft(player);
                    return (
                      <button key={player}
                        onClick={() => left > 0 && handleRaiseChallenge(player)}
                        disabled={working || left <= 0}
                        style={{
                          flex: 1, padding: '0.6rem 0.4rem',
                          background: left > 0 ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${left > 0 ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.12)'}`,
                          borderRadius: 10, color: left > 0 ? '#fde68a' : 'rgba(255,255,255,0.3)',
                          fontWeight: 600, fontSize: '0.78rem', cursor: left > 0 && !working ? 'pointer' : 'not-allowed',
                          opacity: left <= 0 ? 0.45 : 1,
                        }}>
                        ⚡ {name.split(' ')[0]}<br />
                        <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                          {left > 0 ? `${left} challenge${left === 1 ? '' : 's'} left` : 'No challenges left'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Start next game */}
              <button
                onClick={handleNextGame}
                disabled={working}
                style={{
                  width: '100%', padding: '0.85rem', borderRadius: 12,
                  fontWeight: 800, fontSize: '1rem', cursor: working ? 'not-allowed' : 'pointer',
                  background: 'white', color: '#0D9488', border: 'none', opacity: working ? 0.7 : 1,
                }}>
                {working ? 'Starting…' : `▶ Start ${sportConfig.gameLabel} ${score.currentGame}`}
              </button>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem', marginTop: '0.6rem', marginBottom: 0 }}>
                Challenges reset at the start of each game
              </p>
            </div>
          </div>
        </>
      )}

      {/* ── Active challenge overlay ──────────────────────────────────────────── */}
      {score?.activeChallenge && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 60 }} />
          <div style={{
            position: 'fixed', inset: 0, zIndex: 61,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem',
          }}>
            <div style={{
              background: '#134E4A', border: '2px solid rgba(251,191,36,0.6)',
              borderRadius: 20, padding: '2rem', maxWidth: 380, width: '100%', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.4rem' }}>⚡</div>
              <h2 style={{ color: '#fde68a', margin: '0 0 0.4rem', fontWeight: 800, fontSize: '1.3rem' }}>
                Challenge in Progress
              </h2>
              <p style={{ color: 'white', fontWeight: 700, fontSize: '1rem', margin: '0 0 0.25rem' }}>
                {score.activeChallenge.player === 'p1' ? p1Name : p2Name} is challenging the last call
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: '0 0 1.75rem' }}>
                {sportConfig.gameLabel} {score.activeChallenge.gameNumber} · Review in progress
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  onClick={() => handleChallengeResult('successful')}
                  disabled={working}
                  style={{
                    padding: '0.9rem', background: '#22c55e', color: 'white', border: 'none',
                    borderRadius: 12, fontWeight: 700, fontSize: '1rem',
                    cursor: working ? 'not-allowed' : 'pointer', opacity: working ? 0.7 : 1,
                  }}>
                  ✓ Successful — Reverse the call
                </button>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', margin: '0' }}>
                  Last point undone · Challenge count unchanged
                </p>

                <button
                  onClick={() => handleChallengeResult('unsuccessful')}
                  disabled={working}
                  style={{
                    padding: '0.9rem', background: 'rgba(239,68,68,0.85)', color: 'white', border: 'none',
                    borderRadius: 12, fontWeight: 700, fontSize: '1rem',
                    cursor: working ? 'not-allowed' : 'pointer', opacity: working ? 0.7 : 1,
                    marginTop: '0.25rem',
                  }}>
                  ✗ Unsuccessful — Call stands
                </button>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', margin: '0' }}>
                  Score unchanged · 1 challenge deducted
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Match-over overlay ───────────────────────────────────────────────── */}
      {score?.matchOver && !score?.activeChallenge && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 52 }} />
          <div style={{
            position: 'fixed', inset: 0, zIndex: 53,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem',
          }}>
            <div style={{
              background: '#134E4A', border: '2px solid rgba(34,197,94,0.5)',
              borderRadius: 20, padding: '2rem', maxWidth: 400, width: '100%', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.4rem' }}>🏆</div>
              <h2 style={{ color: '#22c55e', margin: '0 0 0.3rem', fontWeight: 800, fontSize: '1.4rem' }}>Match Complete</h2>
              <p style={{ color: 'white', fontWeight: 700, fontSize: '1.05rem', margin: '0 0 0.25rem' }}>
                {score.winner === 'p1' ? p1Name : p2Name} wins
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem', margin: '0 0 1.5rem' }}>
                {sportConfig.gamesLabel}: {p1Name} {gamesWon.p1} – {gamesWon.p2} {p2Name}
              </p>

              {/* Challenge the match-winning point */}
              {score.history?.length > 0 && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Challenge match-ending call?
                  </p>
                  <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '0.5rem' }}>
                    {[{ player: 'p1', name: p1Name }, { player: 'p2', name: p2Name }].map(({ player, name }) => {
                      const left = challengesLeft(player);
                      return (
                        <button key={player}
                          onClick={() => left > 0 && handleRaiseChallenge(player)}
                          disabled={working || left <= 0}
                          style={{
                            flex: 1, padding: '0.6rem 0.4rem',
                            background: left > 0 ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)',
                            border: `1px solid ${left > 0 ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.12)'}`,
                            borderRadius: 10, color: left > 0 ? '#fde68a' : 'rgba(255,255,255,0.3)',
                            fontWeight: 600, fontSize: '0.78rem',
                            cursor: left > 0 && !working ? 'pointer' : 'not-allowed',
                            opacity: left <= 0 ? 0.45 : 1,
                          }}>
                          ⚡ {name.split(' ')[0]}<br />
                          <span style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                            {left > 0 ? `${left} challenge${left === 1 ? '' : 's'} left` : 'No challenges left'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Save match result */}
              <button
                onClick={handleEnd}
                disabled={working}
                style={{
                  width: '100%', padding: '0.85rem', borderRadius: 12,
                  fontWeight: 800, fontSize: '1rem', cursor: working ? 'not-allowed' : 'pointer',
                  background: '#22c55e', color: 'white', border: 'none', opacity: working ? 0.7 : 1,
                }}>
                {working ? 'Saving…' : '✓ Save Match Result'}
              </button>
              <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem', marginTop: '0.6rem', marginBottom: 0 }}>
                This will advance the bracket and notify players
              </p>
            </div>
          </div>
        </>
      )}

      {/* ── End match confirmation ────────────────────────────────────────────── */}
      {showEndConfirm && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100 }} onClick={() => setShowEndConfirm(false)} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#134E4A', borderRadius: '16px 16px 0 0', padding: '1.5rem', zIndex: 101 }}>
            {score?.matchOver ? (
              <>
                <h3 style={{ color: 'white', margin: '0 0 0.5rem', fontWeight: 800 }}>Save Match Result?</h3>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', margin: '0 0 1.25rem' }}>
                  Winner: <strong style={{ color: '#22c55e' }}>{score.winner === 'p1' ? p1Name : p2Name}</strong>.
                  This will update the bracket and notify players.
                </p>
              </>
            ) : (
              <>
                <h3 style={{ color: 'white', margin: '0 0 0.5rem', fontWeight: 800 }}>End Match Early?</h3>
                <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', margin: '0 0 1.25rem' }}>
                  The match is not over. You can only save a completed match. Use offline score entry if needed.
                </p>
              </>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowEndConfirm(false)}
                style={{ flex: 1, padding: '0.7rem', background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
                Cancel
              </button>
              {score?.matchOver && (
                <button onClick={handleEnd} disabled={working}
                  style={{ flex: 1, padding: '0.7rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: working ? 'not-allowed' : 'pointer', fontSize: '0.9rem', opacity: working ? 0.7 : 1 }}>
                  {working ? 'Saving…' : '✓ Save Result'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LiveScoringPage;
